/**
 * MenuPublic — Public Digital Menu Page
 * Uber Eats / Rappi style experience for business menus
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Plus, Minus, X, MapPin, Clock, Phone, Mail,
  Truck, Store, ChevronRight, Check, Loader2, AlertCircle,
  Search, Globe, ArrowLeft, Navigation, Move, ExternalLink,
  ShoppingBag, Trash2, CreditCard, Banknote, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { API_URL } from '../../config';
import {
  getPublicMenu,
  getPublicMenuProducts,
  getPublicMenuProduct,
  validateMenuDelivery,
  createMenuOrder,
  getMyMenuOrders,
} from '../../services/api';

// Leaflet for delivery map
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const deliveryIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

// Day names
const DAY_NAMES = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
};
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const getBranchOpenStatus = (branch) => {
  if (!branch) return { is_open: true, message: 'Disponible' };
  if (branch.is_manually_closed) return { is_open: false, message: 'Cerrado temporalmente' };
  const now = new Date();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = dayNames[now.getDay()];
  const currentTime = now.toTimeString().slice(0, 5);
  const hours = branch.business_hours;
  if (!hours) return { is_open: true, message: 'Abierto' };
  const daySchedule = hours[currentDay];
  if (!daySchedule || !daySchedule.is_open) return { is_open: false, message: 'Cerrado hoy' };
  const openTime = daySchedule.open_time || '09:00';
  const closeTime = daySchedule.close_time || '18:00';
  if (currentTime < openTime) return { is_open: false, message: `Abre a las ${openTime}` };
  if (currentTime >= closeTime) return { is_open: false, message: 'Cerrado' };
  return { is_open: true, message: `Abierto · Cierra a las ${closeTime}` };
};

// ==================== MAP COMPONENTS ====================

const DraggableMarker = ({ position, onPositionChange }) => {
  const markerRef = useRef(null);
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const p = marker.getLatLng();
        onPositionChange({ lat: p.lat, lng: p.lng });
      }
    },
  };
  return <Marker draggable eventHandlers={eventHandlers} position={[position.lat, position.lng]} ref={markerRef} icon={deliveryIcon} />;
};

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
};

const RecenterMap = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords) map.flyTo([coords.lat, coords.lng], 16, { duration: 1 });
  }, [coords, map]);
  return null;
};

const DeliveryLocationMap = ({ coords, onCoordsChange }) => {
  const mapCenter = coords ? [coords.lat, coords.lng] : [19.4326, -99.1332];
  const handlePositionChange = async (newCoords) => {
    onCoordsChange(newCoords);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${newCoords.lat}&lon=${newCoords.lng}&addressdetails=1`);
      const data = await res.json();
      if (data.display_name) onCoordsChange(newCoords, data.display_name);
    } catch {}
  };
  return (
    <div className="relative rounded-lg overflow-hidden border-2 border-slate-200">
      <MapContainer center={mapCenter} zoom={16} style={{ height: '220px', width: '100%' }} scrollWheelZoom>
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <RecenterMap coords={coords} />
        {coords && <DraggableMarker position={coords} onPositionChange={handlePositionChange} />}
        <MapClickHandler onMapClick={handlePositionChange} />
      </MapContainer>
      <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 text-xs text-slate-600 flex items-center gap-2">
        <Move className="w-4 h-4 text-blue-500" />
        <span>Toca el mapa o arrastra el marcador</span>
      </div>
    </div>
  );
};

// ==================== MODIFIER GROUP PICKER ====================

const ModifierGroupPicker = ({ group, groupIndex, selections, onSelect }) => {
  const isRadio = group.max_selections === 1;
  const isRequired = group.min_selections > 0;
  const selected = selections[groupIndex] || [];
  const ruleText = isRequired
    ? (isRadio ? 'Elige 1' : `Elige ${group.min_selections}${group.max_selections > group.min_selections ? ` a ${group.max_selections}` : ''}`)
    : (isRadio ? 'Opcional' : `Hasta ${group.max_selections}`);
  const handleToggle = (option) => {
    if (isRadio) { onSelect(groupIndex, [option]); }
    else {
      const exists = selected.find(s => s.name === option.name);
      if (exists) onSelect(groupIndex, selected.filter(s => s.name !== option.name));
      else if (selected.length < group.max_selections) onSelect(groupIndex, [...selected, option]);
    }
  };
  return (
    <div className="p-4 rounded-xl border-2 border-slate-200 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-800">{group.name}</h4>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${isRequired ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>{ruleText}</span>
      </div>
      <div className="space-y-2">
        {(group.options || []).map((option) => {
          const isSelected = selected.some(s => s.name === option.name);
          return (
            <label key={option.name} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-green-50 border border-green-400' : 'bg-slate-50 border border-transparent hover:bg-slate-100'}`}>
              <input type={isRadio ? 'radio' : 'checkbox'} name={`mod-${groupIndex}`} checked={isSelected} onChange={() => handleToggle({ name: option.name, price_adjustment: option.price_adjustment || 0 })} className="sr-only" />
              <div className={`w-5 h-5 rounded-${isRadio ? 'full' : 'md'} border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-green-500 bg-green-500' : 'border-slate-300'}`}>
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="flex-1 text-slate-700">{option.name}</span>
              {option.price_adjustment > 0 && <span className="text-sm text-green-700 font-medium">+${option.price_adjustment.toFixed(2)}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ==================== COMBO SLOT PICKER ====================

const ComboSlotPicker = ({ slot, slotIndex, selection, onSelect }) => (
  <div className="p-4 rounded-xl border-2 border-slate-200 bg-white">
    <div className="flex items-center justify-between mb-3">
      <h4 className="font-semibold text-slate-800">{slot.name}</h4>
      {slot.required && <span className="text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-700">Obligatorio</span>}
    </div>
    <div className="space-y-2">
      {(slot.options || []).map((option) => {
        const isSelected = selection?.product_id === option.product_id;
        return (
          <label key={option.product_id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-green-50 border-2 border-green-500' : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'}`}>
            <input type="radio" name={`combo-${slotIndex}`} checked={isSelected} onChange={() => onSelect(slotIndex, { product_id: option.product_id, product_name: option.product_name, price_adjustment: option.price_adjustment || 0, slot_name: slot.name })} className="sr-only" />
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-green-500 bg-green-500' : 'border-slate-300'}`}>
              {isSelected && <Check className="w-3 h-3 text-white" />}
            </div>
            {option.product_image && <img src={`${API_URL}${option.product_image}`} alt="" className="w-8 h-8 rounded object-cover" />}
            <span className="flex-1 text-slate-700">{option.product_name}</span>
            {option.price_adjustment > 0 && <span className="text-sm text-green-700 font-medium">+${option.price_adjustment.toFixed(2)}</span>}
          </label>
        );
      })}
    </div>
  </div>
);

// ==================== PRODUCT MODAL ====================

const MenuProductModal = ({ slug, productId, onClose, onAddToCart }) => {
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [comboSlots, setComboSlots] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [modSelections, setModSelections] = useState({});
  const [comboSelections, setComboSelections] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState('');

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    try {
      const res = await getPublicMenuProduct(slug, productId);
      setProduct(res.data.product);
      setModifierGroups(res.data.modifier_groups || []);
      setComboSlots(res.data.combo_slots || []);
    } catch {
      toast.error('No se pudo cargar el producto');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleModSelect = (groupIndex, options) => {
    setModSelections({ ...modSelections, [groupIndex]: options });
  };

  const handleComboSelect = (slotIndex, selection) => {
    setComboSelections({ ...comboSelections, [slotIndex]: selection });
  };

  const calculatePrice = () => {
    if (!product) return 0;
    const pricing = product.pricing || {};
    const isCombo = product.is_combo;
    let base = isCombo ? product.combo_price : (product.retail_price || pricing.retail_price || 0);
    if (!base) base = product.retail_price || pricing.retail_price || 0;

    const modAdj = Object.values(modSelections).flat().reduce((sum, opt) => sum + (opt.price_adjustment || 0), 0);
    const comboAdj = Object.values(comboSelections).reduce((sum, sel) => sum + (sel?.price_adjustment || 0), 0);

    return ((base || 0) + modAdj + comboAdj) * quantity;
  };

  // Validate required modifiers
  const isValid = () => {
    for (let i = 0; i < modifierGroups.length; i++) {
      const g = modifierGroups[i];
      if (g.min_selections > 0 && (!modSelections[i] || modSelections[i].length < g.min_selections)) return false;
    }
    for (let i = 0; i < comboSlots.length; i++) {
      if (comboSlots[i].required && !comboSelections[i]) return false;
    }
    return true;
  };

  const handleAdd = () => {
    if (!isValid()) { toast.error('Completa las selecciones requeridas'); return; }

    const pricing = product.pricing || {};
    const isCombo = product.is_combo;
    let base = isCombo ? product.combo_price : (product.retail_price || pricing.retail_price || 0);
    if (!base) base = product.retail_price || pricing.retail_price || 0;
    const modAdj = Object.values(modSelections).flat().reduce((sum, opt) => sum + (opt.price_adjustment || 0), 0);
    const comboAdj = Object.values(comboSelections).reduce((sum, sel) => sum + (sel?.price_adjustment || 0), 0);
    const unitPrice = (base || 0) + modAdj + comboAdj;

    const item = {
      product_id: product.id,
      product_name: product.name,
      product_image: product.thumbnail_url || product.image_url,
      quantity,
      unit_price: unitPrice,
      subtotal: unitPrice * quantity,
      is_combo: !!isCombo,
      modifier_selections: modifierGroups.map((g, i) => ({
        group_name: g.name,
        options: (modSelections[i] || []).map(o => ({ name: o.name, price_adjustment: o.price_adjustment }))
      })).filter(g => g.options.length > 0),
      combo_selections: Object.values(comboSelections).filter(Boolean),
      special_instructions: specialInstructions,
    };

    onAddToCart(item);
    onClose();
  };

  if (loading) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
    </div>
  );

  if (!product) return null;

  const imgUrl = product.image_url ? `${API_URL}${product.image_url}` : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Image */}
        {imgUrl && <img src={imgUrl} alt={product.name} className="w-full h-48 sm:h-56 object-cover sm:rounded-t-2xl" />}

        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <h2 className="text-xl font-bold text-slate-800">{product.name}</h2>
            <button onClick={onClose} className="p-1 -mr-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>

          {product.description_short && <p className="text-sm text-slate-500 mb-4">{product.description_short}</p>}

          {/* Modifier Groups */}
          {modifierGroups.length > 0 && (
            <div className="space-y-3 mb-4">
              {modifierGroups.map((g, i) => (
                <ModifierGroupPicker key={i} group={g} groupIndex={i} selections={modSelections} onSelect={handleModSelect} />
              ))}
            </div>
          )}

          {/* Combo Slots */}
          {comboSlots.length > 0 && (
            <div className="space-y-3 mb-4">
              {comboSlots.map((s, i) => (
                <ComboSlotPicker key={i} slot={s} slotIndex={i} selection={comboSelections[i]} onSelect={handleComboSelect} />
              ))}
            </div>
          )}

          {/* Special Instructions */}
          <div className="mb-4">
            <label className="text-sm font-medium text-slate-600 mb-1 block">Instrucciones especiales</label>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Ej: Sin cebolla, extra picante..."
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none h-16"
              maxLength={200}
            />
          </div>

          {/* Quantity + Add */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-slate-100 rounded-full px-1">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 text-slate-600 hover:text-slate-800"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-bold text-slate-800">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="p-2 text-slate-600 hover:text-slate-800"><Plus className="w-4 h-4" /></button>
            </div>
            <button
              onClick={handleAdd}
              disabled={!isValid()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart className="w-5 h-5" />
              Agregar ${calculatePrice().toFixed(2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== AUTH MODAL (inline) ====================

const AuthModal = ({ onClose, onSuccess, returnSlug }) => {
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', phone: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && form.password.length < 6) { toast.error('Contraseña mínima 6 caracteres'); return; }
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await api.post('/marketplace/customers/login', { email: form.email, password: form.password });
        localStorage.setItem('customer_token', res.data.token);
        localStorage.setItem('customer_data', JSON.stringify(res.data.customer));
        toast.success('¡Bienvenido!');
        onSuccess();
      } else {
        await api.post('/marketplace/customers/register', form);
        const res = await api.post('/marketplace/customers/login', { email: form.email, password: form.password });
        localStorage.setItem('customer_token', res.data.token);
        localStorage.setItem('customer_data', JSON.stringify(res.data.customer));
        toast.success('¡Cuenta creada!');
        onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (mode === 'login' ? 'Correo o contraseña incorrectos' : 'No se pudo crear la cuenta'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">{mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Nombre completo" required className="w-full px-4 py-3 border rounded-xl text-sm" />
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Teléfono" className="w-full px-4 py-3 border rounded-xl text-sm" />
            </>
          )}
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo electrónico" required className="w-full px-4 py-3 border rounded-xl text-sm" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Contraseña" required className="w-full px-4 py-3 border rounded-xl text-sm" />
          <button type="submit" disabled={loading} className="w-full py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'login' ? 'Entrar' : 'Registrarme'}
          </button>
        </form>
        <p className="text-center text-sm text-slate-500 mt-4">
          {mode === 'login' ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}{' '}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-teal-600 font-medium hover:underline">
            {mode === 'login' ? 'Regístrate' : 'Inicia sesión'}
          </button>
        </p>
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================

const MenuPublic = () => {
  const { slug: rawSlug } = useParams();
  const slug = rawSlug?.replace(/^@/, '');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [menuData, setMenuData] = useState(null);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Cart state (local)
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Product modal
  const [selectedProductId, setSelectedProductId] = useState(null);

  // Checkout
  const [showCheckout, setShowCheckout] = useState(false);
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [paymentMethod, setPaymentMethod] = useState('cash_on_pickup');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCoords, setDeliveryCoords] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryValidation, setDeliveryValidation] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Auth
  const [showAuth, setShowAuth] = useState(false);
  const [customer, setCustomer] = useState(null);

  // Orders
  const [showOrders, setShowOrders] = useState(false);
  const [orders, setOrders] = useState([]);

  const categoryRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('customer_data');
    if (stored) setCustomer(JSON.parse(stored));
    loadMenu();
  }, [slug]);

  useEffect(() => {
    if (menuData) loadProducts();
  }, [menuData, activeCategory, searchQuery, page]);

  const loadMenu = async () => {
    try {
      setLoading(true);
      const res = await getPublicMenu(slug);
      setMenuData(res.data);
      // Pre-fill customer info
      const stored = localStorage.getItem('customer_data');
      if (stored) {
        const c = JSON.parse(stored);
        setCustomerName(c.full_name || '');
        setCustomerPhone(c.phone || '');
      }
    } catch {
      toast.error('Menú no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const params = { page, limit: 50 };
      if (activeCategory) params.category = activeCategory;
      if (searchQuery) params.search = searchQuery;
      const res = await getPublicMenuProducts(slug, params);
      setProducts(res.data.products || []);
      setTotalPages(res.data.pages || 1);
    } catch {}
  };

  // Cart helpers
  const addToCart = (item) => {
    setCart((prev) => {
      const fingerprint = JSON.stringify({ pid: item.product_id, mods: item.modifier_selections, combos: item.combo_selections, si: item.special_instructions });
      const idx = prev.findIndex((c) => JSON.stringify({ pid: c.product_id, mods: c.modifier_selections, combos: c.combo_selections, si: c.special_instructions }) === fingerprint);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + item.quantity, subtotal: updated[idx].unit_price * (updated[idx].quantity + item.quantity) };
        return updated;
      }
      return [...prev, item];
    });
    toast.success(`${item.product_name} agregado`);
  };

  const updateCartQty = (idx, delta) => {
    setCart((prev) => {
      const updated = [...prev];
      const newQty = updated[idx].quantity + delta;
      if (newQty <= 0) { updated.splice(idx, 1); return updated; }
      updated[idx] = { ...updated[idx], quantity: newQty, subtotal: updated[idx].unit_price * newQty };
      return updated;
    });
  };

  const removeFromCart = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
  const cartTotal = cart.reduce((s, i) => s + i.subtotal, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  // Delivery validation
  const handleDeliveryCoordsChange = async (coords, address) => {
    setDeliveryCoords(coords);
    if (address) setDeliveryAddress(address);
    try {
      const res = await validateMenuDelivery(slug, { lat: coords.lat, lng: coords.lng });
      setDeliveryValidation(res.data);
      if (res.data.available) {
        setDeliveryFee(res.data.fee || 0);
      } else {
        setDeliveryFee(0);
        toast.error(res.data.message || 'Fuera del área de entrega');
      }
    } catch {
      setDeliveryValidation(null);
      setDeliveryFee(0);
    }
  };

  // Use geolocation
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Tu navegador no soporta geolocalización'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => handleDeliveryCoordsChange({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error('No se pudo obtener tu ubicación')
    );
  };

  // Checkout submit
  const handleCheckout = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) { setShowAuth(true); return; }
    if (!customerName || !customerPhone) { toast.error('Ingresa tu nombre y teléfono'); return; }
    if (deliveryType === 'delivery' && !deliveryCoords) { toast.error('Selecciona tu ubicación en el mapa'); return; }
    if (deliveryType === 'delivery' && deliveryValidation && !deliveryValidation.available) { toast.error('Tu ubicación está fuera del área de entrega'); return; }

    setSubmitting(true);
    try {
      const data = {
        items: cart.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          modifier_selections: i.modifier_selections,
          combo_selections: i.combo_selections,
          special_instructions: i.special_instructions,
        })),
        delivery_type: deliveryType,
        payment_method: paymentMethod,
        customer_name: customerName,
        customer_phone: customerPhone,
        delivery_address: deliveryAddress,
        delivery_location: deliveryCoords,
        notes,
      };
      await createMenuOrder(slug, token, data);
      toast.success('¡Pedido creado exitosamente!');
      setCart([]);
      setShowCheckout(false);
      setShowCart(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear el pedido');
    } finally {
      setSubmitting(false);
    }
  };

  // Load orders
  const loadOrders = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) { setShowAuth(true); return; }
    try {
      const res = await getMyMenuOrders(slug, token);
      setOrders(res.data || []);
      setShowOrders(true);
    } catch {
      toast.error('Error al cargar pedidos');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
    </div>
  );

  if (!menuData) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white text-slate-500 p-8">
      <AlertCircle className="w-12 h-12 mb-4" />
      <h1 className="text-xl font-bold mb-2">Menú no encontrado</h1>
      <p className="text-sm">Verifica la dirección e intenta de nuevo</p>
    </div>
  );

  if (menuData.menu_closed) {
    const biz = menuData.business || {};
    const closedLogoUrl = biz.logo_thumbnail_url
      ? `${API_URL}${biz.logo_thumbnail_url}`
      : biz.logo_url
        ? `${API_URL}${biz.logo_url}`
        : null;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-8">
        {closedLogoUrl ? (
          <img src={closedLogoUrl} alt={biz.name || ''} className="w-24 h-24 rounded-2xl object-cover mb-6 shadow-lg" />
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-teal-100 flex items-center justify-center mb-6 shadow-lg">
            <Store className="w-10 h-10 text-teal-600" />
          </div>
        )}
        {biz.name && <h1 className="text-2xl font-bold text-slate-800 mb-3">{biz.name}</h1>}
        <p className="text-slate-500 text-center max-w-sm">
          {menuData.menu_closed_message || 'Menú cerrado por el restaurante, comunícate con tu restaurante para habilitarlo nuevamente'}
        </p>
      </div>
    );
  }

  const { menu, business, branch, categories, license_status } = menuData;
  const openStatus = getBranchOpenStatus(branch);
  const bannerUrl = menu?.banner_image ? `${API_URL}${menu.banner_image}` : null;
  const logoUrl = business?.logo_url ? `${API_URL}${business.logo_url}` : null;
  const logoThumb = business?.logo_thumbnail_url ? `${API_URL}${business.logo_thumbnail_url}` : logoUrl;
  const footer = menu?.footer || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header / Banner */}
      <header className="relative">
        {bannerUrl ? (
          <div className="h-44 sm:h-56 bg-slate-200 relative">
            <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          </div>
        ) : (
          <div className="h-32 sm:h-40 bg-gradient-to-r from-teal-600 to-emerald-600" />
        )}
        {/* Logo + info card */}
        <div className="max-w-3xl mx-auto px-4 -mt-12 relative z-10">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-4 flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-white shadow border border-slate-100 overflow-hidden flex-shrink-0">
              {logoThumb ? <img src={logoThumb} alt="" className="w-full h-full object-cover" /> : (
                <div className="w-full h-full bg-teal-100 flex items-center justify-center text-teal-600 font-bold text-2xl">
                  {business.name?.[0]}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 truncate">{business.name}</h1>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5 text-sm">
                <span className={`flex items-center gap-1 font-medium ${openStatus.is_open ? 'text-green-600' : 'text-red-500'}`}>
                  <span className={`w-2 h-2 rounded-full ${openStatus.is_open ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`} />
                  {openStatus.message}
                </span>
                {branch?.delivery_enabled && <span className="flex items-center gap-1 text-teal-600"><Truck className="w-3.5 h-3.5" />Delivery</span>}
                {branch?.pickup_enabled && <span className="flex items-center gap-1 text-slate-400"><Store className="w-3.5 h-3.5" />Recoger</span>}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Category Tabs + Search */}
      <div className="sticky top-0 z-20 bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4">
          {/* Search */}
          <div className="py-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                placeholder="Buscar en el menú..."
                className="w-full pl-10 pr-4 py-2 bg-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-200"
              />
            </div>
          </div>
          {/* Categories */}
          {categories && categories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 scrollbar-hide" ref={categoryRef}>
              <button
                onClick={() => { setActiveCategory(null); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  !activeCategory ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Todo
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setPage(1); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === cat ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {products.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <ShoppingBag className="w-12 h-12 mx-auto mb-3" />
            <p className="font-medium">No hay productos disponibles</p>
          </div>
        ) : !activeCategory && !searchQuery ? (
          // "Todo" view: grouped by category
          (() => {
            const grouped = {};
            products.forEach((p) => {
              const cat = p.category || 'Otros';
              if (!grouped[cat]) grouped[cat] = [];
              grouped[cat].push(p);
            });
            // Sort categories matching the order from the API
            const catOrder = categories || [];
            const sortedCats = Object.keys(grouped).sort((a, b) => {
              const ia = catOrder.indexOf(a);
              const ib = catOrder.indexOf(b);
              if (ia === -1 && ib === -1) return a.localeCompare(b);
              if (ia === -1) return 1;
              if (ib === -1) return -1;
              return ia - ib;
            });
            return (
              <div className="space-y-8">
                {sortedCats.map((cat) => (
                  <section key={cat}>
                    <h2 className="text-xl font-bold text-slate-800 mb-4 pb-2 border-b-2 border-teal-500/30">{cat}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {grouped[cat].map((product) => {
                        const pricing = product.pricing || {};
                        const price = product.is_combo ? product.combo_price : (product.retail_price || pricing.retail_price || 0);
                        const thumbUrl = product.thumbnail_url ? `${API_URL}${product.thumbnail_url}` : (product.image_url ? `${API_URL}${product.image_url}` : null);
                        const outOfStock = product.in_stock === false;
                        const hasModifiers = (product.modifier_groups?.length || 0) > 0;
                        return (
                          <button
                            key={product.id}
                            onClick={() => !outOfStock && setSelectedProductId(product.id)}
                            className={`flex bg-white rounded-xl border overflow-hidden text-left transition-shadow hover:shadow-md ${outOfStock ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div className="flex-1 p-4">
                              <h3 className="font-semibold text-slate-800 text-sm line-clamp-2">{product.name}</h3>
                              {product.description_short && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{product.description_short}</p>}
                              <p className="text-teal-600 font-bold mt-2">${(price || 0).toFixed(2)}</p>
                              {hasModifiers && <span className="text-xs text-slate-400">Personalizable</span>}
                              {outOfStock && <span className="text-xs text-red-500 font-medium">Agotado</span>}
                            </div>
                            {thumbUrl && (
                              <div className="w-28 h-28 flex-shrink-0">
                                <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            );
          })()
        ) : (
          // Filtered view (single category or search)
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {products.map((product) => {
              const pricing = product.pricing || {};
              const price = product.is_combo ? product.combo_price : (product.retail_price || pricing.retail_price || 0);
              const thumbUrl = product.thumbnail_url ? `${API_URL}${product.thumbnail_url}` : (product.image_url ? `${API_URL}${product.image_url}` : null);
              const outOfStock = product.in_stock === false;
              const hasModifiers = (product.modifier_groups?.length || 0) > 0;

              return (
                <button
                  key={product.id}
                  onClick={() => !outOfStock && setSelectedProductId(product.id)}
                  className={`flex bg-white rounded-xl border overflow-hidden text-left transition-shadow hover:shadow-md ${outOfStock ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex-1 p-4">
                    <h3 className="font-semibold text-slate-800 text-sm line-clamp-2">{product.name}</h3>
                    {product.description_short && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{product.description_short}</p>}
                    <p className="text-teal-600 font-bold mt-2">${(price || 0).toFixed(2)}</p>
                    {hasModifiers && <span className="text-xs text-slate-400">Personalizable</span>}
                    {outOfStock && <span className="text-xs text-red-500 font-medium">Agotado</span>}
                  </div>
                  {thumbUrl && (
                    <div className="w-28 h-28 flex-shrink-0">
                      <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`w-9 h-9 rounded-lg text-sm font-medium ${page === i + 1 ? 'bg-teal-600 text-white' : 'bg-white border text-slate-600 hover:bg-slate-50'}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Floating Cart Button */}
      {cartCount > 0 && !showCart && !showCheckout && (
        <div className="fixed bottom-0 left-0 right-0 z-30 p-4 bg-gradient-to-t from-white via-white/90 to-transparent">
          <button
            onClick={() => setShowCart(true)}
            className="w-full max-w-3xl mx-auto flex items-center justify-between px-6 py-4 bg-teal-600 text-white rounded-2xl shadow-xl hover:bg-teal-700"
          >
            <div className="flex items-center gap-3">
              <div className="bg-teal-500 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">{cartCount}</div>
              <span className="font-semibold">Ver carrito</span>
            </div>
            <span className="font-bold text-lg">${cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Cart Drawer */}
      {showCart && !showCheckout && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/50" onClick={() => setShowCart(false)}>
          <div className="bg-white w-full max-w-lg rounded-t-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Tu Pedido</h2>
              <button onClick={() => setShowCart(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Tu carrito está vacío</p>
              ) : cart.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                  {item.product_image && (
                    <img src={`${API_URL}${item.product_image}`} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-800 text-sm truncate">{item.product_name}</h4>
                    {item.modifier_selections?.filter(g => g.options.length).map((g, gi) => (
                      <p key={gi} className="text-xs text-slate-400 truncate">{g.options.map(o => o.name).join(', ')}</p>
                    ))}
                    {item.special_instructions && <p className="text-xs text-slate-400 italic truncate">{item.special_instructions}</p>}
                    <p className="text-teal-600 font-bold text-sm mt-0.5">${item.subtotal.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateCartQty(idx, -1)} className="p-1.5 bg-white rounded-lg border text-slate-500 hover:text-slate-700"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-7 text-center text-sm font-bold">{item.quantity}</span>
                    <button onClick={() => updateCartQty(idx, 1)} className="p-1.5 bg-white rounded-lg border text-slate-500 hover:text-slate-700"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => removeFromCart(idx)} className="p-1.5 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="p-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-600 font-medium">Subtotal</span>
                  <span className="text-lg font-bold text-slate-800">${cartTotal.toFixed(2)}</span>
                </div>
                <button
                  onClick={() => { setShowCart(false); setShowCheckout(true); }}
                  className="w-full py-3.5 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 flex items-center justify-center gap-2"
                >
                  Continuar al pago
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Panel */}
      {showCheckout && (
        <div className="fixed inset-0 z-40 bg-white overflow-y-auto">
          <div className="max-w-lg mx-auto p-4">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => { setShowCheckout(false); setShowCart(true); }} className="p-2 rounded-lg hover:bg-slate-100">
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <h2 className="text-xl font-bold text-slate-800">Finalizar Pedido</h2>
            </div>

            {/* Customer Info */}
            <div className="space-y-4 mb-6">
              <h3 className="font-semibold text-slate-700">Tus Datos</h3>
              <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Nombre completo" className="w-full px-4 py-3 border rounded-xl text-sm" />
              <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Teléfono" className="w-full px-4 py-3 border rounded-xl text-sm" />
            </div>

            {/* Delivery Type */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-700 mb-3">Tipo de Entrega</h3>
              <div className="grid grid-cols-2 gap-3">
                {branch?.pickup_enabled !== false && (
                  <button
                    onClick={() => { setDeliveryType('pickup'); setDeliveryFee(0); }}
                    className={`p-4 rounded-xl border-2 text-center transition-colors ${deliveryType === 'pickup' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <Store className={`w-6 h-6 mx-auto mb-1 ${deliveryType === 'pickup' ? 'text-teal-600' : 'text-slate-400'}`} />
                    <p className={`text-sm font-medium ${deliveryType === 'pickup' ? 'text-teal-700' : 'text-slate-600'}`}>Recoger</p>
                  </button>
                )}
                {branch?.delivery_enabled && (
                  <button
                    onClick={() => setDeliveryType('delivery')}
                    className={`p-4 rounded-xl border-2 text-center transition-colors ${deliveryType === 'delivery' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <Truck className={`w-6 h-6 mx-auto mb-1 ${deliveryType === 'delivery' ? 'text-teal-600' : 'text-slate-400'}`} />
                    <p className={`text-sm font-medium ${deliveryType === 'delivery' ? 'text-teal-700' : 'text-slate-600'}`}>Domicilio</p>
                  </button>
                )}
              </div>
            </div>

            {/* Delivery Address + Map */}
            {deliveryType === 'delivery' && (
              <div className="mb-6 space-y-3">
                <h3 className="font-semibold text-slate-700">Dirección de Entrega</h3>
                <button onClick={useMyLocation} className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700">
                  <Navigation className="w-4 h-4" /> Usar mi ubicación
                </button>
                <DeliveryLocationMap coords={deliveryCoords} onCoordsChange={handleDeliveryCoordsChange} />
                <input type="text" value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Dirección completa" className="w-full px-4 py-3 border rounded-xl text-sm" />
                {deliveryValidation && (
                  <div className={`flex items-center gap-2 text-sm ${deliveryValidation.available ? 'text-green-600' : 'text-red-500'}`}>
                    {deliveryValidation.available ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {deliveryValidation.available
                      ? `Envío: $${deliveryValidation.fee?.toFixed(2)} (${deliveryValidation.distance_km} km)`
                      : deliveryValidation.message || 'Fuera del área de entrega'}
                  </div>
                )}
              </div>
            )}

            {/* Payment method */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-700 mb-3">Método de Pago</h3>
              <div className="space-y-2">
                {deliveryType === 'pickup' ? (
                  <>
                    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer ${paymentMethod === 'cash_on_pickup' ? 'border-teal-500 bg-teal-50' : 'border-slate-200'}`}>
                      <input type="radio" name="pay" checked={paymentMethod === 'cash_on_pickup'} onChange={() => setPaymentMethod('cash_on_pickup')} className="sr-only" />
                      <Banknote className="w-5 h-5 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Efectivo al recoger</span>
                    </label>
                    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer ${paymentMethod === 'card_on_pickup' ? 'border-teal-500 bg-teal-50' : 'border-slate-200'}`}>
                      <input type="radio" name="pay" checked={paymentMethod === 'card_on_pickup'} onChange={() => setPaymentMethod('card_on_pickup')} className="sr-only" />
                      <CreditCard className="w-5 h-5 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Tarjeta al recoger</span>
                    </label>
                  </>
                ) : (
                  <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer border-teal-500 bg-teal-50`}>
                    <Banknote className="w-5 h-5 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Efectivo contra entrega</span>
                  </label>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-700 mb-2">Notas</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Instrucciones adicionales..."
                className="w-full px-4 py-3 border rounded-xl text-sm resize-none h-20"
                maxLength={300}
              />
            </div>

            {/* Order Summary */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-2">
              <h3 className="font-semibold text-slate-700 mb-2">Resumen</h3>
              {cart.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-slate-600">{item.quantity}x {item.product_name}</span>
                  <span className="font-medium">${item.subtotal.toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                {deliveryType === 'delivery' && deliveryFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Envío</span>
                    <span>${deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mt-1">
                  <span>Total</span>
                  <span className="text-teal-600">${(cartTotal + deliveryFee).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleCheckout}
              disabled={submitting || cart.length === 0}
              className="w-full py-4 bg-teal-600 text-white rounded-xl font-bold text-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2 mb-8"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingBag className="w-5 h-5" />}
              {submitting ? 'Enviando...' : 'Confirmar Pedido'}
            </button>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {selectedProductId && (
        <MenuProductModal
          slug={slug}
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
          onAddToCart={addToCart}
        />
      )}

      {/* Auth Modal */}
      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={() => {
            setShowAuth(false);
            const stored = localStorage.getItem('customer_data');
            if (stored) {
              const c = JSON.parse(stored);
              setCustomer(c);
              setCustomerName(c.full_name || customerName);
              setCustomerPhone(c.phone || customerPhone);
            }
          }}
          returnSlug={slug}
        />
      )}

      {/* Orders Modal */}
      {showOrders && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          <div className="max-w-lg mx-auto p-4">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setShowOrders(false)} className="p-2 rounded-lg hover:bg-slate-100"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <h2 className="text-xl font-bold text-slate-800">Mis Pedidos</h2>
            </div>
            {orders.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <ShoppingBag className="w-12 h-12 mx-auto mb-3" />
                <p>No tienes pedidos aún</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white rounded-xl border p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-slate-800 text-sm">{order.order_number}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'preparing' ? 'bg-orange-100 text-orange-700' :
                        order.status === 'ready' ? 'bg-green-100 text-green-700' :
                        order.status === 'delivered' ? 'bg-slate-100 text-slate-600' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {order.status === 'pending' ? 'Pendiente' :
                         order.status === 'confirmed' ? 'Confirmado' :
                         order.status === 'preparing' ? 'Preparando' :
                         order.status === 'ready' ? 'Listo' :
                         order.status === 'delivered' ? 'Entregado' :
                         order.status === 'cancelled' ? 'Cancelado' : order.status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 space-y-0.5">
                      {order.items?.map((it, i) => <p key={i}>{it.quantity}x {it.product_name}</p>)}
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t">
                      <span className="text-xs text-slate-400">{new Date(order.created_at).toLocaleString()}</span>
                      <span className="font-bold text-teal-600">${order.total?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 mt-auto">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-4">
            {logoThumb && <img src={logoThumb} alt="" className="w-10 h-10 rounded-lg" />}
            <h3 className="text-white font-bold text-lg">{business.name}</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
            {footer.address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{footer.address}</span>
              </div>
            )}
            {(footer.phone || business.phone) && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <a href={`tel:${footer.phone || business.phone}`} className="hover:text-white">{footer.phone || business.phone}</a>
              </div>
            )}
            {(footer.email || business.email) && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href={`mailto:${footer.email || business.email}`} className="hover:text-white">{footer.email || business.email}</a>
              </div>
            )}
          </div>

          {/* Schedule */}
          {branch?.business_hours && (
            <div className="mb-6">
              <h4 className="text-slate-300 font-medium text-sm mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> Horarios</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {DAY_ORDER.map((day) => {
                  const schedule = branch.business_hours[day];
                  return (
                    <div key={day} className="flex justify-between">
                      <span className="text-slate-400">{DAY_NAMES[day]}</span>
                      <span className={schedule?.is_open ? 'text-slate-300' : 'text-slate-500'}>
                        {schedule?.is_open ? `${schedule.open_time} - ${schedule.close_time}` : 'Cerrado'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Social Links */}
          {footer.social_links?.length > 0 && (
            <div className="flex gap-3 mb-6">
              {footer.social_links.map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-slate-800 rounded-lg text-xs hover:bg-slate-700 capitalize">
                  {link.platform}
                </a>
              ))}
            </div>
          )}

          {/* Bottom bar */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-800 text-xs">
            <span>Creado con <a href="https://todoenlocal.com" className="text-teal-400 hover:text-teal-300 font-medium">TodoEnLocal</a></span>
            {customer && (
              <button onClick={loadOrders} className="text-teal-400 hover:text-teal-300 font-medium">Mis Pedidos</button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MenuPublic;
